import { UserJourneyData } from "@/types/dataSchema";

/**
 * Finds the first question in a journey by analyzing incoming connections.
 * The first question is the one that has no incoming connections
 * (neither deeperQuestionId nor nextTopicQuestionId pointing to it).
 *
 * @param journeyData The complete journey data
 * @returns The first question, or null if no questions exist
 */
export function findFirstQuestion(
  journeyData: UserJourneyData,
): UserJourneyData["questions"][number] | null {
  if (journeyData.questions.length === 0) {
    return null;
  }

  // Collect all question IDs that are referenced as targets
  const referencedQuestionIds = new Set<string>();

  journeyData.questions.forEach((question) => {
    // Add deeperQuestionId targets
    question.options.forEach((option) => {
      if (option.deeperQuestionId) {
        referencedQuestionIds.add(option.deeperQuestionId);
      }
    });

    // Add nextTopicQuestionId targets
    if (question.nextTopicQuestionId) {
      referencedQuestionIds.add(question.nextTopicQuestionId);
    }
  });

  // Find questions that are NOT referenced (no incoming connections)
  const entryQuestions = journeyData.questions.filter(
    (question) => !referencedQuestionIds.has(question.id),
  );

  // Find the main flow question: look for "entry_reason" first as it's the designed entry point
  const mainEntryQuestion = entryQuestions.find((q) => q.id === "entry_reason");

  if (mainEntryQuestion) {
    return mainEntryQuestion;
  }

  // If no entry_reason found, find the question with the most connections (part of main flow)
  const questionWithMostConnections = entryQuestions.reduce(
    (best, current) => {
      const currentConnections = getQuestionConnectionCount(
        current,
        journeyData,
      );
      const bestConnections = best
        ? getQuestionConnectionCount(best, journeyData)
        : 0;

      return currentConnections > bestConnections ? current : best;
    },
    null as UserJourneyData["questions"][number] | null,
  );

  // Return the first entry question found, or fallback to first in array
  return (
    questionWithMostConnections || entryQuestions[0] || journeyData.questions[0]
  );
}

/**
 * Gets the number of outgoing connections from a question (how connected it is to the flow)
 */
function getQuestionConnectionCount(
  question: UserJourneyData["questions"][number],
  journeyData: UserJourneyData,
): number {
  let connections = 0;

  // Count deeperQuestionId connections
  question.options.forEach((option) => {
    if (option.deeperQuestionId) connections++;
  });

  // Count nextTopicQuestionId connections
  if (question.nextTopicQuestionId) connections++;

  return connections;
}

/**
 * Gets all orphaned questions (questions with no incoming AND no meaningful outgoing connections).
 * These are questions that are disconnected from the main flow.
 *
 * @param journeyData The complete journey data
 * @returns Array of orphaned questions
 */
export function getOrphanedQuestions(
  journeyData: UserJourneyData,
): UserJourneyData["questions"][number][] {
  if (journeyData.questions.length === 0) {
    return [];
  }

  // Collect all question IDs that are referenced as targets
  const referencedQuestionIds = new Set<string>();

  journeyData.questions.forEach((question) => {
    // Add deeperQuestionId targets
    question.options.forEach((option) => {
      if (option.deeperQuestionId) {
        referencedQuestionIds.add(option.deeperQuestionId);
      }
    });

    // Add nextTopicQuestionId targets
    if (question.nextTopicQuestionId) {
      referencedQuestionIds.add(question.nextTopicQuestionId);
    }
  });

  // Find questions that are NOT referenced (no incoming connections)
  const entryQuestions = journeyData.questions.filter(
    (question) => !referencedQuestionIds.has(question.id),
  );

  // From entry questions, find those that are truly orphaned (no meaningful outgoing connections)
  const orphanedQuestions = entryQuestions.filter((question) => {
    // Skip entry_reason as it's the main entry point
    if (question.id === "entry_reason") return false;

    // Check if question has meaningful outgoing connections
    const hasOutgoingConnections =
      getQuestionConnectionCount(question, journeyData) > 0;

    // If no outgoing connections, it's truly orphaned
    return !hasOutgoingConnections;
  });

  return orphanedQuestions;
}

/**
 * Gets all entry questions (questions with no incoming connections).
 * Useful for debugging or when there might be multiple disconnected flows.
 *
 * @param journeyData The complete journey data
 * @returns Array of questions that have no incoming connections
 */
export function findAllEntryQuestions(
  journeyData: UserJourneyData,
): UserJourneyData["questions"] {
  if (journeyData.questions.length === 0) {
    return [];
  }

  // Collect all question IDs that are referenced as targets
  const referencedQuestionIds = new Set<string>();

  journeyData.questions.forEach((question) => {
    // Add deeperQuestionId targets
    question.options.forEach((option) => {
      if (option.deeperQuestionId) {
        referencedQuestionIds.add(option.deeperQuestionId);
      }
    });

    // Add nextTopicQuestionId targets
    if (question.nextTopicQuestionId) {
      referencedQuestionIds.add(question.nextTopicQuestionId);
    }
  });

  // Return all questions that are NOT referenced
  return journeyData.questions.filter(
    (question) => !referencedQuestionIds.has(question.id),
  );
}
